import React, { useEffect, useState } from 'react'
import Swal from 'sweetalert2'
import { useFormik } from 'formik'
import { registerFormSchema } from '../../utils/formSchema/formSchemas'
import {
  Box,
  Button,
  Typography,
  Checkbox,
  TextField,
  InputAdornment,
  IconButton,
  Container,
  Paper,
  useTheme,
  Stack,
  Divider,
  alpha,
  LinearProgress,
  Link,
} from '@mui/material'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import {
  User,
  Mail,
  Lock,
  Eye,
  EyeOff,
  Shield,
  CheckCircle,
  Zap,
  Clock,
  Globe,
} from 'lucide-react'
import axios from 'axios'

const ModernRegister = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [vaidateOnChangeState, setValidateOnChangeState] = useState(false)
  const [vaidateOnBlurState, setValidateOnBlurState] = useState(true)

  const submit = async (values) => {
    setIsLoading(true)
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await axios.post(`${process.env.REACT_APP_API_URL}auth/register`, {
        name: values.username,
        email: values.email,
        password: values.password,
        timezone
      })

      Swal.fire({
        title: 'Kayıt Başarılı!',
        html: `
          <p>Hoş geldiniz! Hesabınız başarıyla oluşturuldu.</p>
          <p style="margin-top: 12px; font-size: 0.9em; color: #666;">
            Lütfen e-posta adresinize gelen doğrulama bağlantısına tıklayarak 
            hesabınızı aktifleştirin.
          </p>
        `,
        icon: 'success',
        confirmButtonText: 'Giriş Sayfasına Git',
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
        confirmButtonColor: theme.palette.primary.main,
      })

      navigate('/login')
    } catch (error) {
      setIsLoading(false)
      Swal.fire({
        title: 'Kayıt Başarısız',
        text: error.response?.data?.message || 'Kayıt işlemi sırasında bir hata oluştu',
        icon: 'error',
        confirmButtonText: 'Tamam',
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
        confirmButtonColor: theme.palette.primary.main,
      })
    }
  }

  const { values, errors, handleChange, handleSubmit } = useFormik({
    initialValues: {
      username: '',
      email: '',
      password: '',
      agree: false,
    },
    validationSchema: registerFormSchema,
    onSubmit: submit,
    validateOnChange: vaidateOnChangeState,
    validateOnBlur: vaidateOnBlurState,
  })

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      setValidateOnChangeState(true)
      setValidateOnBlurState(false)
    }
  }, [errors])

  const benefits = [
    { icon: Zap, text: '7/24 Monitoring', color: '#10b981' },
    { icon: Shield, text: 'SSL Tracking', color: '#3b82f6' },
    { icon: Clock, text: 'Instant Alerts', color: '#f59e0b' },
    { icon: Globe, text: 'Multi-Region', color: '#8b5cf6' },
  ]

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        position: 'relative',
        overflow: 'hidden',
        background: theme.palette.background.default,
      }}
    >
      {/* Animated Background */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0,
        }}
      >
        {/* Gradient Orbs */}
        {[...Array(4)].map((_, i) => (
          <Box
            key={i}
            component={motion.div}
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.2, 0.4, 0.2],
              x: [0, 60, 0],
              y: [0, -40, 0],
            }}
            transition={{
              duration: 10 + i * 2,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: i * 1.5,
            }}
            sx={{
              position: 'absolute',
              width: 350,
              height: 350,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${alpha(
                i % 2 === 0 ? theme.palette.primary.main : theme.palette.primary.light,
                0.15
              )}, transparent)`,
              filter: 'blur(60px)',
              top: `${15 + i * 25}%`,
              left: `${5 + i * 25}%`,
            }}
          />
        ))}

        {/* Grid Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              linear-gradient(${alpha(theme.palette.primary.main, 0.08)} 1px, transparent 1px),
              linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.08)} 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
            opacity: 0.4,
          }}
        />
      </Box>

      {/* Left Panel - Benefits */}
      <Box
        sx={{
          flex: 1,
          display: { xs: 'none', lg: 'flex' },
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          p: 6,
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Box
          component={motion.div}
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          sx={{ maxWidth: 500 }}
        >
          {/* Logo & Title */}
          <Box sx={{ mb: 6 }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 900,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2,
              }}
            >
              Başlayın
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: alpha('#fff', 0.8),
                fontWeight: 600,
                mb: 2,
              }}
            >
              İzlemeye Ücretsiz Başlayın
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: alpha('#fff', 0.6),
                lineHeight: 1.8,
              }}
            >
              Kredi kartı gerekmez. 7 gün ücretsiz deneme ile tüm özelliklere
              anında erişin.
            </Typography>
          </Box>

          {/* Benefits */}
          <Stack spacing={3}>
            {benefits.map((benefit, idx) => {
              const Icon = benefit.icon
              return (
                <Box
                  key={idx}
                  component={motion.div}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 + idx * 0.1 }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 2.5,
                    borderRadius: 2,
                    background: alpha(theme.palette.background.paper, 0.3),
                    backdropFilter: 'blur(10px)',
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateX(10px)',
                      background: alpha(theme.palette.background.paper, 0.5),
                      border: `1px solid ${alpha(benefit.color, 0.3)}`,
                    },
                  }}
                >
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      background: alpha(benefit.color, 0.15),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon size={24} color={benefit.color} />
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{
                      color: alpha('#fff', 0.9),
                      fontWeight: 600,
                    }}
                  >
                    {benefit.text}
                  </Typography>
                </Box>
              )
            })}
          </Stack>

          {/* Testimonial */}
          <Box
            sx={{
              mt: 6,
              p: 3,
              borderRadius: 3,
              background: alpha(theme.palette.background.paper, 0.3),
              backdropFilter: 'blur(10px)',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: alpha('#fff', 0.8),
                fontStyle: 'italic',
                mb: 2,
                lineHeight: 1.6,
              }}
            >
              "RahatUp sayesinde sistemlerimizi 7/24 izliyoruz. Kesinti olmadan,
              rahatça iş yapıyoruz. Kesinlikle tavsiye ederim!"
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: '1.2rem',
                }}
              >
                A
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#fff' }}>
                  Ahmet Yılmaz
                </Typography>
                <Typography variant="caption" sx={{ color: alpha('#fff', 0.6) }}>
                  CTO, TechCorp
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Right Panel - Register Form */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 2, md: 6 },
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Container maxWidth="sm">
          <Paper
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            elevation={0}
            sx={{
              p: { xs: 3, md: 5 },
              borderRadius: 4,
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.background.paper,
                0.9
              )} 0%, ${alpha(theme.palette.background.paper, 0.7)} 100%)`,
              backdropFilter: 'blur(20px)',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              boxShadow: `0 20px 60px ${alpha(theme.palette.primary.main, 0.1)}`,
            }}
          >
            {/* Header */}
            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 900,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1,
                }}
              >
                Hesap Oluştur
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: alpha('#fff', 0.6),
                }}
              >
                Ücretsiz hesabınızı oluşturun ve hemen izlemeye başlayın
              </Typography>
            </Box>

            {/* Form */}
            <Stack spacing={3}>
              {/* Username */}
              <Box>
                <TextField
                  id="username"
                  fullWidth
                  label="Kullanıcı Adı"
                  placeholder="Adınız Soyadınız"
                  value={values.username}
                  onChange={handleChange}
                  error={!!errors.username}
                  helperText={errors.username}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <User size={20} color={theme.palette.primary.main} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.default, 0.5),
                      borderRadius: 2,
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.2),
                      },
                      '&:hover fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.4),
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                  }}
                />
              </Box>

              {/* Email */}
              <Box>
                <TextField
                  id="email"
                  fullWidth
                  label="E-posta"
                  placeholder="ornek@email.com"
                  value={values.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Mail size={20} color={theme.palette.primary.main} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.default, 0.5),
                      borderRadius: 2,
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.2),
                      },
                      '&:hover fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.4),
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                  }}
                />
              </Box>

              {/* Password */}
              <Box>
                <TextField
                  id="password"
                  fullWidth
                  type={showPassword ? 'text' : 'password'}
                  label="Şifre"
                  placeholder="••••••••"
                  value={values.password}
                  onChange={handleChange}
                  error={!!errors.password}
                  helperText={errors.password}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock size={20} color={theme.palette.primary.main} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{
                            color: alpha('#fff', 0.6),
                            '&:hover': {
                              color: theme.palette.primary.main,
                            },
                          }}
                        >
                          {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.default, 0.5),
                      borderRadius: 2,
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.2),
                      },
                      '&:hover fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.4),
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                  }}
                />
              </Box>

              {/* Terms Agreement */}
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                  }}
                >
                  <Checkbox
                    id="agree"
                    checked={values.agree}
                    onChange={handleChange}
                    sx={{
                      color: alpha(theme.palette.primary.main, 0.5),
                      '&.Mui-checked': {
                        color: theme.palette.primary.main,
                      },
                      mt: -0.5,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: alpha('#fff', 0.7),
                      lineHeight: 1.6,
                    }}
                  >
                    <Link
                      href="https://uptime-test-api.rahatyonetim.com/privasyPolisy.html"
                      target="_blank"
                      sx={{
                        color: theme.palette.primary.main,
                        textDecoration: 'none',
                        fontWeight: 600,
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      Gizlilik Politikası
                    </Link>
                    {' '}ve{' '}
                    <Link
                      href="https://uptime-test-api.rahatyonetim.com/privasyPolisy.html"
                      target="_blank"
                      sx={{
                        color: theme.palette.primary.main,
                        textDecoration: 'none',
                        fontWeight: 600,
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      Kullanım Şartları
                    </Link>
                    'nı kabul ediyorum
                  </Typography>
                </Box>
                {errors.agree && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.error.main,
                      ml: 4.5,
                      display: 'block',
                      mt: 0.5,
                    }}
                  >
                    {errors.agree}
                  </Typography>
                )}
              </Box>

              {/* Register Button */}
              <Button
                fullWidth
                variant="contained"
                onClick={handleSubmit}
                disabled={isLoading}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 700,
                  color: '#000',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                    transform: 'translateY(-2px)',
                    boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.4)}`,
                  },
                  '&:disabled': {
                    background: alpha(theme.palette.primary.main, 0.5),
                    color: alpha('#000', 0.5),
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                {isLoading ? 'Hesap Oluşturuluyor...' : 'Hesap Oluştur'}
              </Button>

              {isLoading && <LinearProgress sx={{ borderRadius: 1 }} />}

              {/* Divider */}
              <Divider
                sx={{
                  my: 2,
                  '&::before, &::after': {
                    borderColor: alpha(theme.palette.primary.main, 0.2),
                  },
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: alpha('#fff', 0.5),
                    px: 2,
                  }}
                >
                  VEYA
                </Typography>
              </Divider>

              {/* Login Link */}
              <Box
                sx={{
                  textAlign: 'center',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: alpha('#fff', 0.6),
                    display: 'inline',
                  }}
                >
                  Zaten hesabınız var mı?{' '}
                </Typography>
                <Button
                  onClick={() => navigate('/login')}
                  sx={{
                    color: theme.palette.primary.main,
                    textTransform: 'none',
                    fontWeight: 700,
                    p: 0,
                    minWidth: 'auto',
                    '&:hover': {
                      background: 'transparent',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Giriş Yap
                </Button>
              </Box>
            </Stack>
          </Paper>

          {/* Features List */}
          <Box
            sx={{
              mt: 3,
              display: 'flex',
              justifyContent: 'center',
              gap: 3,
              flexWrap: 'wrap',
            }}
          >
            {['7 Gün Deneme', 'Kredi Kartı Yok', 'Her An İptal'].map((text, idx) => (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 0.5,
                  borderRadius: 2,
                  background: alpha(theme.palette.success.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                }}
              >
                <CheckCircle size={14} color={theme.palette.success.main} />
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.success.main,
                    fontWeight: 600,
                  }}
                >
                  {text}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>
    </Box>
  )
}

export default ModernRegister